import { useEffect, useRef } from 'react';
import { useCartStore } from '@/stores/cartStore';
import { useShakatahCartStore, clearAllCheckoutMarkers } from '@/stores/shakatahCartStore';

// MARKER EXPIRATION: Only clear markers after this many minutes
// This prevents premature marker clearing when user returns from checkout
const MARKER_EXPIRATION_MINUTES = 10;

// Check for checkout markers and clear cart if present
// CRITICAL: Only clear markers if they're EXPIRED or we're on a checkout-related page
function checkAndClearOnMarkers(clearShakatahCart: () => void): boolean {
  const checkoutInProgress = localStorage.getItem('shakatah-checkout-in-progress');
  const splitCompleted = localStorage.getItem('shakatah-split-completed');
  const sessionCompleted = sessionStorage.getItem('shakatah-checkout-completed-this-session');
  
  if (checkoutInProgress || splitCompleted || sessionCompleted) {
    console.log('useCartSync: Checkout markers found');
    
    // Check if we're on a checkout-related page - if so, DON'T clear markers yet
    const currentPath = window.location.pathname;
    const isCheckoutPage = currentPath.includes('/shakatah-checkout') || 
                           currentPath.includes('/shakatah-split') ||
                           currentPath.includes('/split-setup') ||
                           currentPath.includes('/split-payment') ||
                           currentPath.includes('/split/');
    
    // Always clear cart state on any page if markers exist
    console.log('useCartSync: Clearing cart state (markers present)');
    clearShakatahCart();
    
    // Clear cart storage keys
    try {
      localStorage.removeItem('shakatah-split-cart');
      sessionStorage.removeItem('shakatah-imported-cart-hash');
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('shakatah-split-cart-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(function(key) { localStorage.removeItem(key); });
    } catch (e) {
      console.error('Error clearing cart storage:', e);
    }
    
    if (isCheckoutPage) {
      console.log('useCartSync: On checkout page, KEEPING markers for import protection');
      return true;
    }
    
    // Not on checkout page - check if markers are old enough to clear
    let markerTimestamp: number | null = null;
    try {
      const timestampStr = localStorage.getItem('shakatah-checkout-timestamp');
      if (timestampStr) {
        markerTimestamp = parseInt(timestampStr, 10);
      }
    } catch (e) {
      console.error('Error parsing marker timestamp:', e);
    }
    
    const now = Date.now();
    const markerAge = markerTimestamp ? (now - markerTimestamp) / (1000 * 60) : MARKER_EXPIRATION_MINUTES + 1;
    
    if (markerAge >= MARKER_EXPIRATION_MINUTES) {
      console.log('useCartSync: Markers expired (' + markerAge.toFixed(1) + ' min old), clearing all markers');
      clearAllCheckoutMarkers();
      localStorage.removeItem('shakatah-checkout-timestamp');
    } else {
      console.log('useCartSync: Markers still fresh (' + markerAge.toFixed(1) + ' min old), KEEPING for import protection');
    }
    
    return true;
  }
  return false;
}

/**
 * Hook to sync cart state with Shopify after checkout.
 * Clears local cart if the Shopify cart no longer exists or is empty
 * (indicating checkout was completed).
 * 
 * This hook should be used in the main App/layout component.
 */
export function useCartSync() {
  const syncRegularCart = useCartStore(function(state) { return state.syncCart; });
  const syncShakatahCart = useShakatahCartStore(function(state) { return state.syncCart; });
  const clearShakatahCart = useShakatahCartStore(function(state) { return state.clearCart; });
  const regularHasHydrated = useCartStore(function(state) { return state._hasHydrated; });
  const shakatahHasHydrated = useShakatahCartStore(function(state) { return state._hasHydrated; });
  
  // Track if we've done the initial sync
  const hasInitialSyncRef = useRef(false);

  // Sync after both stores are hydrated (initial load only)
  useEffect(function() {
    if (regularHasHydrated && shakatahHasHydrated && !hasInitialSyncRef.current) {
      hasInitialSyncRef.current = true;
      console.log('Both stores hydrated, running initial cart sync...');
      
      // First check for checkout markers
      if (!checkAndClearOnMarkers(clearShakatahCart)) {
        // No markers, proceed with normal sync
        syncRegularCart();
        syncShakatahCart();
      }
    }
  }, [regularHasHydrated, shakatahHasHydrated, syncRegularCart, syncShakatahCart, clearShakatahCart]);

  // Sync when user returns to the tab (only if hydrated)
  useEffect(function() {
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        const regularStore = useCartStore.getState();
        const shakatahStore = useShakatahCartStore.getState();
        
        if (regularStore._hasHydrated && shakatahStore._hasHydrated) {
          console.log('Tab became visible, checking for checkout markers...');
          
          if (!checkAndClearOnMarkers(clearShakatahCart)) {
            console.log('No markers found, syncing carts normally...');
            syncRegularCart();
            syncShakatahCart();
          }
        } else {
          console.log('Tab visible but stores not yet hydrated, skipping sync');
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return function() {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [syncRegularCart, syncShakatahCart, clearShakatahCart]);
  
  // Also sync when the page is focused (catches edge cases)
  useEffect(function() {
    function handleFocus() {
      const regularStore = useCartStore.getState();
      const shakatahStore = useShakatahCartStore.getState();
      
      if (regularStore._hasHydrated && shakatahStore._hasHydrated) {
        checkAndClearOnMarkers(clearShakatahCart);
      }
    }
    
    window.addEventListener('focus', handleFocus);
    
    return function() {
      window.removeEventListener('focus', handleFocus);
    };
  }, [clearShakatahCart]);
}
